import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { notificationsService } from "@b2b/services";
import type { HeaderNotifications } from "@b2b/ui-components";
import { useAuth } from "../auth/Authprovider";
import { B2B_NOTIFICATION_UNREAD_REFRESH_EVENT } from "./notificationUnreadEvents";

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  data?: Record<string, unknown>;
  createdAt: string;
};

type Ctx =
  | { mode: "active"; data: HeaderNotifications }
  | { mode: "inactive" };

const NotificationHeaderContext = createContext<Ctx | undefined>(undefined);

function resolveNotificationPath(
  data: Record<string, unknown> | undefined,
  userRole: string | null | undefined,
): string | null {
  if (!data) return null;
  const eventType = String(data.eventType || "");
  switch (eventType) {
    case "PRODUCT_CREATED":
      if (userRole === "admin" || userRole === "super_admin") {
        return "/admin/products/list";
      }
      if (userRole === "seller") {
        return "/seller/product_management";
      }
      return null;
    default:
      return null;
  }
}

export function NotificationHeaderProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, userRole } = useAuth();
  const navigate = useNavigate();

  const eligible = isAuthenticated && !isLoading;

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const syncUnreadCount = useCallback(async () => {
    try {
      const data = await notificationsService.unreadCount();
      setUnreadCount(Number(data.unreadCount || 0));
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const load = useCallback(async () => {
    if (!eligible) return;
    setLoading(true);
    try {
      const [listData, countData] = await Promise.all([
        notificationsService.list({ page: 1, limit: 20 }),
        notificationsService.unreadCount(),
      ]);
      setItems((listData.items || []) as NotificationItem[]);
      setUnreadCount(Number(countData.unreadCount || 0));
    } catch {
      setItems([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [eligible]);

  useEffect(() => {
    if (!eligible) {
      setItems([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    void syncUnreadCount();
    const interval = window.setInterval(() => void syncUnreadCount(), 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void syncUnreadCount();
    };
    const onRefresh = () => void syncUnreadCount();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(B2B_NOTIFICATION_UNREAD_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(B2B_NOTIFICATION_UNREAD_REFRESH_EVENT, onRefresh);
    };
  }, [eligible, syncUnreadCount]);

  const onMarkAllRead = useCallback(async () => {
    const prevItems = itemsRef.current;
    const prevUnread = prevItems.filter((item) => !item.isRead).length;
    setItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
    try {
      await notificationsService.markAllRead();
    } catch {
      setItems(prevItems);
      setUnreadCount(prevUnread);
    }
  }, []);

  const onOpenNotification = useCallback(
    async (notificationId: string) => {
      const notification = itemsRef.current.find((item) => item.id === notificationId);
      if (!notification) return;

      if (!notification.isRead) {
        setItems((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true } : item,
          ),
        );
        setUnreadCount((count) => Math.max(0, count - 1));
        await notificationsService.markRead(notificationId).catch(() => {});
      }

      const path = resolveNotificationPath(notification.data, userRole ?? null);
      if (path) navigate(path);
    },
    [navigate, userRole],
  );

  const value = useMemo((): Ctx => {
    if (!eligible) return { mode: "inactive" };
    return {
      mode: "active",
      data: {
        unreadCount,
        items: items.map(({ id, title, message, isRead, createdAt }) => ({
          id,
          title,
          message,
          isRead,
          createdAt,
        })),
        loading,
        refresh: load,
        onOpenNotification,
        onMarkAllRead,
      },
    };
  }, [eligible, unreadCount, items, loading, load, onOpenNotification, onMarkAllRead]);

  return (
    <NotificationHeaderContext.Provider value={value}>
      {children}
    </NotificationHeaderContext.Provider>
  );
}

export function useNotificationHeader(): HeaderNotifications | undefined {
  const ctx = useContext(NotificationHeaderContext);
  if (!ctx || ctx.mode !== "active") return undefined;
  return ctx.data;
}

export function useNotificationHeaderCount() {
  const notifications = useNotificationHeader();
  return notifications?.unreadCount ?? 0;
}
